import React, { useState, useRef } from 'react';
import { ChevronDown, Check } from 'lucide-react';

interface AccountValueMultiselectProps {
  selectedValues: string[];
  onChange: (values: string[]) => void;
  isFetching?: boolean;
  className?: string;
}

const AccountValueMultiselect: React.FC<AccountValueMultiselectProps> = ({ 
  selectedValues, 
  onChange, 
  isFetching = false,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showTopShadow, setShowTopShadow] = useState(false);
  const [showBottomShadow, setShowBottomShadow] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const accountValueOptions = [
    'Strategic',
    'Enterprise', 
    'Midmarket',
    'Corporate',
    'SMB',
    'Small Business'
  ];

  const handleToggleValue = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(v => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    setShowTopShadow(target.scrollTop > 0);
    setShowBottomShadow(target.scrollTop < target.scrollHeight - target.clientHeight);
  };

  const isFiltered = selectedValues.length > 0;

  return (
    <div className={`relative ${className}`}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`rounded-xl px-3 py-2 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent cursor-pointer text-sm min-h-[2.5rem] flex items-center justify-between shadow-sm font-inter transition-all duration-200 ${
          isFiltered
            ? 'bg-gray-50 dark:bg-gray-600 border border-purple-200 dark:border-purple-700'
            : 'bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600'
        }`}
      >
        <div className="flex items-center flex-1 mr-2">
          <div className="flex-1">
            {selectedValues.length === 0 ? (
              <span className="text-gray-900 dark:text-gray-100">All Account Values</span>
            ) : (
              <div className="flex flex-wrap gap-1">
                {selectedValues.map(value => (
                  <span key={value} className="bg-purple-600 text-white px-2 py-1 rounded-lg text-xs font-medium font-inter">
                    {value}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <ChevronDown className={`h-4 w-4 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''} ${
          isFiltered ? 'text-purple-500' : 'text-gray-500 dark:text-gray-400'
        }`} />
      </div>

      {isOpen && (
        <div className="absolute top-full mt-1 left-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-[9999] min-w-[280px]">
          {/* Top scroll shadow indicator */}
          {showTopShadow && (
            <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-b from-gray-100 dark:from-gray-700 to-transparent pointer-events-none z-10 rounded-t-xl" />
          )}
          
          {/* Bottom scroll shadow indicator */}
          {showBottomShadow && (
            <div className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-t from-gray-100 dark:from-gray-700 to-transparent pointer-events-none z-10 rounded-b-xl" />
          )}
          
          <div 
            ref={dropdownRef}
            className="max-h-80 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400 transition-colors"
            onScroll={handleScroll}
          >
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <div
                onClick={handleClearAll}
                className={`px-3 py-2 text-sm font-medium font-inter text-right transition-all ${
                  selectedValues.length > 0
                    ? 'text-orange-600 hover:bg-orange-100 dark:hover:bg-orange-900/30 cursor-pointer'
                    : 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                }`}
              >
                Clear All
              </div>
            </div>
            
            <div className="py-1">
              {accountValueOptions.map(value => {
                const isSelected = selectedValues.includes(value);
                return (
                  <div
                    key={value}
                    onClick={() => handleToggleValue(value)}
                    className={`px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer text-sm flex items-center justify-between font-inter ${
                      isSelected ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' : 'text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <span>{value}</span>
                    {isSelected && <Check className="h-4 w-4 text-purple-600 flex-shrink-0" />}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {isOpen && (
        <div 
          className="fixed inset-0 z-[9998]" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default AccountValueMultiselect;
